import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, X, Check, CheckCircle2 } from 'lucide-react';
import { FormDrawer, TextField, TextAreaField, SelectField, Field } from '@/components/FormControls';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { GOAL_CATEGORIES, GOAL_STATUSES } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const emptyGoal = {
  title: '', category: 'Business', description: '', deadline: '',
  status: 'Active', progress: 0, actionSteps: [],
};

// Self-contained create/edit drawer for a goal. Owns its form state and API calls;
// notifies the parent via onSaved() so the list can refresh.
export const GoalFormDrawer = ({ open, onOpenChange, goal, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(emptyGoal);
  const [stepText, setStepText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const editing = Boolean(goal);

  useEffect(() => {
    if (open) {
      setForm(goal ? { ...emptyGoal, ...goal, deadline: goal.deadline || '' } : emptyGoal);
      setStepText('');
    }
  }, [open, goal]);

  const addStep = () => {
    if (!stepText.trim()) return;
    setForm((f) => ({
      ...f,
      actionSteps: [...(f.actionSteps || []), { id: crypto.randomUUID(), text: stepText.trim(), done: false }],
    }));
    setStepText('');
  };

  const toggleStep = (id) => {
    setForm((f) => ({ ...f, actionSteps: f.actionSteps.map((s) => (s.id === id ? { ...s, done: !s.done } : s)) }));
  };

  const removeStep = (id) => {
    setForm((f) => ({ ...f, actionSteps: f.actionSteps.filter((s) => s.id !== id) }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t('goals.titleRequired')); return; }
    setSaving(true);
    try {
      const payload = { ...form, progress: Number(form.progress) || 0, deadline: form.deadline || null };
      if (editing) {
        await api.put(`/goals/${goal.id}`, payload);
        toast.success(t('goals.updated'));
      } else {
        await api.post('/goals', payload);
        toast.success(t('goals.created'));
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(t('goals.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    try {
      await api.put(`/goals/${goal.id}`, { status: 'Completed', progress: 100 });
      toast.success(t('goals.completed'));
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(t('goals.updateError'));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/goals/${deleteId}`);
      toast.success(t('goals.deleted'));
      setDeleteId(null);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(t('goals.deleteError'));
    }
  };

  return (
    <>
      <FormDrawer
        open={open}
        onOpenChange={onOpenChange}
        title={editing ? t('goals.editTitle') : t('goals.newTitle')}
        onSubmit={save}
        loading={saving}
        submitLabel={editing ? t('common.saveChanges') : t('goals.create')}
        submitTestId="goal-create-save-button"
      >
        <TextField label={t('goals.fieldTitle')} testId="goal-title-input" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('goals.titlePlaceholder')} />
        <div className="grid grid-cols-2 gap-3">
          <SelectField label={t('goals.category')} testId="goal-category-select" value={form.category}
            onValueChange={(v) => setForm({ ...form, category: v })} options={GOAL_CATEGORIES} />
          <SelectField label={t('goals.status')} testId="goal-status-select" value={form.status}
            onValueChange={(v) => setForm({ ...form, status: v })} options={GOAL_STATUSES} />
        </div>
        <TextAreaField label={t('goals.description')} testId="goal-description-input" value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t('goals.descriptionPlaceholder')} />
        <TextField label={t('goals.deadlineField')} testId="goal-deadline-input" type="date" value={form.deadline || ''}
          onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
        <Field label={t('goals.progress', { n: form.progress })}>
          <Slider data-testid="goal-progress-slider" value={[Number(form.progress) || 0]}
            onValueChange={(v) => setForm({ ...form, progress: v[0] })} max={100} step={5} className="py-2" />
        </Field>

        <Field label={t('goals.actionSteps')}>
          <div className="space-y-2">
            {(form.actionSteps || []).map((s) => (
              <div key={s.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-2">
                <button onClick={() => toggleStep(s.id)}
                  className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                    s.done ? 'border-emerald-300 bg-emerald-400/20 text-emerald-300' : 'border-white/25 text-transparent')}>
                  <Check className="h-3 w-3" />
                </button>
                <span className={cn('flex-1 text-sm', s.done ? 'text-foreground/40 line-through' : 'text-foreground/85')}>{s.text}</span>
                <button onClick={() => removeStep(s.id)} className="text-foreground/40 hover:text-rose-300"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input data-testid="goal-step-input" value={stepText} onChange={(e) => setStepText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStep(); } }}
                placeholder={t('goals.stepPlaceholder')}
                className="h-10 rounded-xl border-white/10 bg-white/[0.06] text-foreground placeholder:text-foreground/40" />
              <button data-testid="goal-step-add" onClick={addStep}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.08] text-brand-cyan hover:bg-white/[0.12]">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Field>

        {editing ? (
          <div className="flex gap-2 pt-1">
            {goal.status !== 'Completed' ? (
              <button data-testid="goal-complete-button" onClick={markComplete}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-brand-violet/30 bg-brand-violet/10 px-3 py-2.5 text-sm font-medium text-violet-100 hover:bg-brand-violet/15">
                <CheckCircle2 className="h-4 w-4" /> {t('goals.complete')}
              </button>
            ) : null}
            <button data-testid="goal-delete-button" onClick={() => setDeleteId(goal.id)}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-rose-300/20 bg-rose-400/10 px-4 py-2.5 text-sm font-medium text-rose-200 hover:bg-rose-400/15">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </FormDrawer>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="mx-auto max-w-[420px] border-white/10 bg-[#0A1730] text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('goals.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/60">{t('common.cannotUndo')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-white/[0.06] text-foreground hover:bg-white/[0.1]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="goal-delete-confirm" onClick={doDelete} className="bg-rose-500 text-white hover:bg-rose-500/90">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
